import { useMemo } from 'react';
import {
  SHOPIFY_AG1_POUCH_DOUBLE_SUB_VARIANT_ID_V53,
  SHOPIFY_AG1_POUCH_SINGLE_SUB_VARIANT_ID_V53,
  SHOPIFY_AGZ_TRAVEL_PACK_DISCOUNTED_SUB_VARIANT_ID,
  SHOPIFY_AGZ_TRAVEL_PACK_SINGLE_SUB_VARIANT_ID,
  SHOPIFY_OMEGA_SUB_BUNDLE_VARIANT_ID,
  SHOPIFY_TRAVEL_PACK_DOUBLE_SUB_VARIANT_ID_V53,
  SHOPIFY_TRAVEL_PACK_SINGLE_SUB_VARIANT_ID_V53
} from '@portal/config/base';
import { useIsLoggedInValue } from '@portal/appLayer/auth/useIsLoggedInValue';
import { useQueryGetSubscriptionsV2 } from '@portal/state/internal/getSubscriptions';
import { useStoreValue } from '@portal/state/hooks/useStore';

const getVariantIds = (variantIdObject: any, store: string) => {
  const variants = variantIdObject[store];
  if (!variants) {
    return [];
  }
  return Object.values(variants).filter(Boolean) as string[];
};

export function useHasPouchOrPackSubscription() {
  const store = useStoreValue();
  const isLoggedIn = useIsLoggedInValue();
  const { data: shopifySubscriptions = [], isPending, isLoading } = useQueryGetSubscriptionsV2();

  const activeSubscriptions = useMemo(
    () =>
      shopifySubscriptions
        ?.filter((i) => i.is_active)
        .sort((a, b) => a.next_order_date - b.next_order_date) || [],
    [shopifySubscriptions]
  );

  const hasActiveSubscriptions = !!activeSubscriptions.length;

  const hasAnyActiveSubWithPouchOrPackSubscription = useMemo(() => {
    const pouchSingleSubIds = getVariantIds(SHOPIFY_AG1_POUCH_SINGLE_SUB_VARIANT_ID_V53, store);
    const pouchDoubleSubIds = getVariantIds(SHOPIFY_AG1_POUCH_DOUBLE_SUB_VARIANT_ID_V53, store);
    const travelPackSingleSubIds = getVariantIds(
      SHOPIFY_TRAVEL_PACK_SINGLE_SUB_VARIANT_ID_V53,
      store
    );
    const travelPackDoubleSubIds = getVariantIds(
      SHOPIFY_TRAVEL_PACK_DOUBLE_SUB_VARIANT_ID_V53,
      store
    );

    const relevantVariantIds = [
      ...pouchSingleSubIds,
      ...pouchDoubleSubIds,
      ...travelPackSingleSubIds,
      ...travelPackDoubleSubIds
    ];

    return activeSubscriptions.some((subscription) =>
      subscription.line_items.some((item) => {
        const variantId = item.product.shopify_variant_id.split('/').pop();
        return variantId ? relevantVariantIds.includes(variantId) : false;
      })
    );
  }, [activeSubscriptions, store]);

  const omegaSubIds = useMemo(() => {
    const omegaIds = SHOPIFY_OMEGA_SUB_BUNDLE_VARIANT_ID[store];
    return omegaIds ? [omegaIds.subscriptionPrice].filter(Boolean) : [];
  }, [store]);

  const omegaBundleSubIds = useMemo(() => {
    const omegaBundleIds = SHOPIFY_OMEGA_SUB_BUNDLE_VARIANT_ID[store];
    return omegaBundleIds ? [omegaBundleIds.bundlePrice].filter(Boolean) : [];
  }, [store]);

  const agzSubIds = useMemo(
    () => getVariantIds(SHOPIFY_AGZ_TRAVEL_PACK_SINGLE_SUB_VARIANT_ID, store),
    [store]
  );

  const agzBundleSubIds = useMemo(
    () => getVariantIds(SHOPIFY_AGZ_TRAVEL_PACK_DISCOUNTED_SUB_VARIANT_ID, store),
    [store]
  );

  const hasStandaloneOmegaSubscription = useMemo(() => {
    return (
      !hasAnyActiveSubWithPouchOrPackSubscription &&
      activeSubscriptions.some((subscription) =>
        subscription.line_items.some((lineItem) => {
          const variantId = lineItem.product.shopify_variant_id.split('/').pop();
          return variantId ? [...omegaSubIds, ...omegaBundleSubIds].includes(variantId) : false;
        })
      )
    );
  }, [
    activeSubscriptions,
    omegaSubIds,
    hasAnyActiveSubWithPouchOrPackSubscription,
    omegaBundleSubIds
  ]);

  const hasStandaloneAgzSubscription = useMemo(() => {
    return (
      !hasAnyActiveSubWithPouchOrPackSubscription &&
      activeSubscriptions.some((subscription) =>
        subscription.line_items.some((lineItem) => {
          const variantId = lineItem.product.shopify_variant_id.split('/').pop();
          return variantId ? [...agzSubIds, ...agzBundleSubIds].includes(variantId) : false;
        })
      )
    );
  }, [activeSubscriptions, agzSubIds, hasAnyActiveSubWithPouchOrPackSubscription, agzBundleSubIds]);

  if (!isLoggedIn) {
    return {
      hasAnyActiveSubWithPouchOrPackSubscription: false,
      isPending: isLoading,
      hasStandaloneOmegaSubscription: false,
      hasStandaloneAgzSubscription: false,
      hasActiveSubscriptions: false
    };
  }

  return {
    hasAnyActiveSubWithPouchOrPackSubscription,
    isPending,
    hasStandaloneOmegaSubscription,
    hasStandaloneAgzSubscription,
    hasActiveSubscriptions
  };
}
